import { useState, useEffect, useContext, useRef } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { AuthContext } from '@/App';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { useDateRange } from '@/contexts/DateRangeContext';
import DashboardLayout from '@/components/DashboardLayout';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { DollarSign, Eye, MousePointer, Target, Activity } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const META_COLOR = '#1877F2';
const GOOGLE_COLOR = '#EA4335';

export default function Analytics() {
  const { token } = useContext(AuthContext);
  const { clientId } = useWorkspace();
  const { range: dateRange } = useDateRange();
  // Mapeo del label global ("Últimos 90 días") al string que usa el backend ("90d")
  const PRESET_MAP = { 'Hoy':'today', 'Ayer':'yesterday', 'Últimos 7 días':'7d', 'Últimos 15 días':'15d', 'Últimos 30 días':'30d', 'Últimos 90 días':'90d' };
  const clientIdRef = useRef(clientId);
  useEffect(() => { clientIdRef.current = clientId; }, [clientId]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('all');
  const [dailyData, setDailyData] = useState([]);
  const [dailyPlatform, setDailyPlatform] = useState('meta');
  const [dailyLoading, setDailyLoading] = useState(false);
  const dailyRange = PRESET_MAP[dateRange?.label] || '30d';

  useEffect(() => { fetchData(dailyRange); }, [clientId, dailyRange, dateRange?.label]); // eslint-disable-line
  useEffect(() => { fetchDailyTrend(dailyPlatform, dailyRange); }, [clientId, dailyPlatform, dailyRange, dateRange?.label]); // eslint-disable-line

  const fetchData = async (range) => {
    setLoading(true);
    try {
      const cid = clientIdRef.current;
      const cidParam = cid ? `&client_id=${cid}` : '';
      // FIX: ambas plataformas se leen de metrics_daily (no mas /meta/campaigns-live)
      const dbRes = await axios.get(
        `${API}/dashboard/metrics?date_from=${getDateFrom(range||'30d')}&date_to=${getTodayStr()}${cidParam}`,
        { headers: { Authorization: `Bearer ${token}` } }
      ).catch(() => ({data:{campaigns:[]}}));
      const allCamps = dbRes.data?.campaigns || [];
      const metaCamps = allCamps.filter(c => c.platform === 'meta');
      const googleCamps = allCamps.filter(c => c.platform === 'google');
      setCampaigns([...metaCamps, ...googleCamps]);
    } catch (e) {
      console.error('Analytics error:', e);
    } finally {
      setLoading(false);
    }
  };

  const getTodayStr = () => { const d = new Date(); d.setHours(d.getHours()-4); return d.toISOString().slice(0,10); };
  const getDateFrom = (range) => {
    const today = new Date(); today.setHours(today.getHours()-4);
    const map = {'1d':0,'today':0,'ayer':1,'7d':6,'15d':14,'30d':29,'90d':89};
    const days = map[range] ?? 29;
    const d = new Date(today); d.setDate(d.getDate()-days);
    return d.toISOString().slice(0,10);
  };

  const fetchDailyTrend = async (plat, range) => {
    setDailyLoading(true);
    try {
      const cid = clientIdRef.current;
      const cidParam = cid ? `&client_id=${cid}` : '';
      const res = await axios.get(`${API}/analytics/daily-trend?platform=${plat}&range=${range}${cidParam}`, { headers: { Authorization: `Bearer ${token}` } });
      setDailyData(res.data?.days || []);
    } catch(e) { console.error(e); }
    finally { setDailyLoading(false); }
  };

  const filtered = activeView === 'all' ? campaigns : campaigns.filter(c => c.platform === activeView);
  const metaCamps = campaigns.filter(c => c.platform === 'meta');
  const googleCamps = campaigns.filter(c => c.platform === 'google');
  const sum = (arr, key) => arr.reduce((s, c) => s + (parseFloat(c[key]) || 0), 0);
  const avg = (arr, key) => arr.length ? sum(arr, key) / arr.length : 0;
  const ms = { spent: sum(metaCamps,'spent'), impressions: sum(metaCamps,'impressions'), clicks: sum(metaCamps,'clicks'), conversions: sum(metaCamps,'conversions'), ctr: avg(metaCamps,'ctr') };
  const gs = { spent: sum(googleCamps,'spent'), impressions: sum(googleCamps,'impressions'), clicks: sum(googleCamps,'clicks'), conversions: sum(googleCamps,'conversions'), ctr: avg(googleCamps,'ctr') };

  const fmt = (n, type='num') => {
    if (!n && n !== 0) return '—';
    if (type === 'usd') return n >= 1000 ? `$${(n/1000).toFixed(1)}K` : `$${n.toFixed(0)}`;
    if (type === 'pct') return `${n.toFixed(2)}%`;
    return n >= 1000000 ? `${(n/1000000).toFixed(1)}M` : n >= 1000 ? `${(n/1000).toFixed(1)}K` : String(Math.round(n));
  };

  const pieData = [
    { name: 'Meta Ads', value: ms.spent, color: META_COLOR },
    { name: 'Google Ads', value: gs.spent, color: GOOGLE_COLOR },
  ].filter(d => d.value > 0);

  if (loading) return (
    <DashboardLayout hideDatePicker>
      <div className="flex items-center justify-center h-screen">
        <div className="text-xl">Cargando analytics...</div>
      </div>
    </DashboardLayout>
  );

  return (
    <DashboardLayout>
      <div className="p-8">
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <h1 className="font-heading font-bold text-4xl mb-2">Analytics</h1>
          <p className="text-secondary">Analisis detallado{clientId ? ' — cliente activo' : ''}</p>
        </motion.div>

        <div className="flex gap-3 mb-8">
          {[['all','Todas'],['meta','Meta Ads'],['google','Google Ads']].map(([val, label]) => (
            <button key={val} onClick={() => setActiveView(val)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${activeView === val ? 'bg-indigo-600 text-white' : 'glass-card'}`}>
              {label}
            </button>
          ))}
        </div>

        {campaigns.length === 0 ? (
          <div className="glass-card p-12 rounded-xl text-center">
            <Activity className="w-12 h-12 text-secondary mx-auto mb-4" />
            <p className="text-lg font-semibold mb-2">Sin datos de analytics</p>
            <p className="text-secondary">Conecta una fuente de datos para ver analisis</p>
          </div>
        ) : (
          <>
            {/* KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              {[
                { label: 'Gasto Total', meta: ms.spent, google: gs.spent, type: 'usd', icon: DollarSign },
                { label: 'Impresiones', meta: ms.impressions, google: gs.impressions, type: 'num', icon: Eye },
                { label: 'Clicks', meta: ms.clicks, google: gs.clicks, type: 'num', icon: MousePointer },
                { label: 'Conversiones', meta: ms.conversions, google: gs.conversions, type: 'num', icon: Target },
              ].map(({ label, meta, google, type, icon: Icon }) => (
                <motion.div key={label} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                  className="glass-card p-5 rounded-xl">
                  <div className="flex items-center gap-2 mb-3">
                    <Icon className="w-4 h-4 text-secondary" />
                    <span className="text-xs font-semibold uppercase tracking-wider text-secondary">{label}</span>
                  </div>
                  {activeView !== 'google' && (
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-2 h-2 rounded-full" style={{background: META_COLOR}} />
                      <span className="text-lg font-bold text-gray-900 dark:text-white">{fmt(meta, type)}</span>
                      <span className="text-xs text-secondary">Meta</span>
                    </div>
                  )}
                  {activeView !== 'meta' && (
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full" style={{background: GOOGLE_COLOR}} />
                      <span className="text-lg font-bold text-gray-900 dark:text-white">{fmt(google, type)}</span>
                      <span className="text-xs text-secondary">Google</span>
                    </div>
                  )}
                </motion.div>
              ))}
            </div>

            {/* Tendencia Diaria */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading font-bold text-xl">Tendencia Diaria — Últimos 30 días</h2>
                <div className="flex gap-2 items-center">
                  {[['1d','Hoy'],['ayer','Ayer'],['7d','7d'],['15d','15d'],['30d','30d'],['90d','90d']].map(([val,label]) => (
                    <button key={val} onClick={() => setDailyRange(val)}
                      className={`px-3 py-1 rounded-full text-xs font-medium border transition-all ${dailyRange===val ? 'bg-gray-700 text-white border-gray-700' : 'glass-card'}`}>
                      {label}
                    </button>
                  ))}
                  <div className="w-px h-5 bg-gray-300 mx-1"/>
                  {[['meta','Meta',META_COLOR],['google','Google',GOOGLE_COLOR]].map(([val,label,color]) => (
                    <button key={val} onClick={() => setDailyPlatform(val)}
                      style={dailyPlatform===val ? {background:color,color:'white',border:'none'} : {}}
                      className={`px-3 py-1 rounded-full text-xs font-medium border transition-all ${dailyPlatform===val ? '' : 'glass-card'}`}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              {dailyLoading ? (
                <div className="glass-card rounded-2xl p-8 text-center text-secondary">Cargando datos diarios...</div>
              ) : dailyData.length === 0 ? (
                <div className="glass-card rounded-2xl p-8 text-center text-secondary">No hay datos diarios disponibles</div>
              ) : (
                <div className="grid grid-cols-1 gap-6">
                  <div className="glass-card rounded-2xl p-6">
                    <h3 className="font-semibold text-base mb-4 text-gray-900 dark:text-white">Conversiones por día</h3>
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={dailyData} margin={{top:5,right:20,bottom:20,left:0}}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                        <XAxis dataKey="date_label" tick={{fontSize:10}} interval={2} />
                        <YAxis tick={{fontSize:10}} />
                        <Tooltip formatter={(v) => [Math.round(v), 'Conversiones']} />
                        <Bar dataKey="conversions" fill={dailyPlatform==='meta'?META_COLOR:GOOGLE_COLOR} radius={[3,3,0,0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="grid grid-cols-2 gap-6">
                    <div className="glass-card rounded-2xl p-6">
                      <h3 className="font-semibold text-base mb-4 text-gray-900 dark:text-white">Gasto diario</h3>
                      <ResponsiveContainer width="100%" height={200}>
                        <LineChart data={dailyData} margin={{top:5,right:20,bottom:20,left:0}}>
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                          <XAxis dataKey="date_label" tick={{fontSize:10}} interval={2} />
                          <YAxis tick={{fontSize:10}} tickFormatter={v => `$${v>=1000?`${(v/1000).toFixed(0)}K`:v}`} />
                          <Tooltip formatter={(v) => [`$${v.toLocaleString()}`, 'Gasto']} />
                          <Line type="monotone" dataKey="spent" stroke={dailyPlatform==='meta'?META_COLOR:GOOGLE_COLOR} strokeWidth={2} dot={false} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="glass-card rounded-2xl p-6">
                      <h3 className="font-semibold text-base mb-4 text-gray-900 dark:text-white">CTR diario (%)</h3>
                      <ResponsiveContainer width="100%" height={200}>
                        <LineChart data={dailyData} margin={{top:5,right:20,bottom:20,left:0}}>
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                          <XAxis dataKey="date_label" tick={{fontSize:10}} interval={2} />
                          <YAxis tick={{fontSize:10}} tickFormatter={v => `${v.toFixed(1)}%`} />
                          <Tooltip formatter={(v) => [`${v.toFixed(2)}%`, 'CTR']} />
                          <Line type="monotone" dataKey="ctr" stroke="#B6D957" strokeWidth={2} dot={false} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Gráficos */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              <div className="glass-card p-6 rounded-xl">
                <h3 className="font-semibold mb-4">Top 10 Campañas por Gasto</h3>
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart
                    layout="vertical"
                    data={filtered.slice(0,10).map(c => ({
                      name: (c.name||'').substring(0,30),
                      Gasto: parseFloat(c.spent)||0,
                      fill: c.platform==='meta' ? META_COLOR : GOOGLE_COLOR
                    })).sort((a,b) => b.Gasto - a.Gasto)}
                    margin={{top:0,right:60,bottom:0,left:160}}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" horizontal={false} />
                    <XAxis type="number" tick={{fontSize:10}} tickFormatter={v => v>=1000000?`$${(v/1000000).toFixed(1)}M`:v>=1000?`$${(v/1000).toFixed(0)}K`:`$${v}`} />
                    <YAxis type="category" dataKey="name" tick={{fontSize:10}} width={155} />
                    <Tooltip formatter={(v) => [`$${v>=1000000?(v/1000000).toFixed(2)+'M':v>=1000?(v/1000).toFixed(1)+'K':v}`, 'Gasto']} />
                    <Bar dataKey="Gasto" radius={[0,4,4,0]}>
                      {filtered.slice(0,10).sort((a,b)=>(parseFloat(b.spent)||0)-(parseFloat(a.spent)||0)).map((entry,i) => (
                        <Cell key={i} fill={entry.platform==='meta' ? META_COLOR : GOOGLE_COLOR} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                <div className="flex gap-4 mt-2 justify-end">
                  <div className="flex items-center gap-1"><div className="w-3 h-3 rounded-full" style={{background:META_COLOR}}/><span className="text-xs text-gray-500">Meta</span></div>
                  <div className="flex items-center gap-1"><div className="w-3 h-3 rounded-full" style={{background:GOOGLE_COLOR}}/><span className="text-xs text-gray-500">Google</span></div>
                </div>
              </div>
              <div className="glass-card p-6 rounded-xl">
                <h3 className="font-semibold mb-4">Distribucion de gasto</h3>
                {pieData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie data={pieData} cx="50%" cy="50%" outerRadius={80} dataKey="value"
                        label={({name, percent}) => `${name} ${(percent*100).toFixed(0)}%`}>
                        {pieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                      </Pie>
                      <Tooltip formatter={(v) => [`$${(v/1000).toFixed(1)}K`]} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : <p className="text-secondary text-center py-8">Sin datos</p>}
              </div>
            </div>

            {/* Tabla */}
            <div className="glass-card rounded-xl overflow-hidden">
              <div className="p-5 border-b border-white/10 flex items-center justify-between">
                <h3 className="font-semibold">Detalle de campanas ({filtered.length})</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10">
                      {['Campaña','Plataforma','Gasto','Impresiones','Clicks','CTR','Conv.','ROI'].map(h => (
                        <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-secondary">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((c, i) => (
                      <tr key={i} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                        <td className="px-4 py-3">
                          <div className="font-medium text-sm max-w-xs truncate">{c.name}</div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-semibold ${c.platform === 'meta' ? 'bg-blue-500/20 text-blue-400' : 'bg-red-500/20 text-red-400'}`}>
                            {c.platform === 'meta' ? 'Meta' : 'Google'}
                          </span>
                        </td>
                        <td className="px-4 py-3 font-semibold text-indigo-400">{fmt(parseFloat(c.spent),'usd')}</td>
                        <td className="px-4 py-3 text-secondary">{fmt(parseInt(c.impressions))}</td>
                        <td className="px-4 py-3 text-secondary">{fmt(parseInt(c.clicks))}</td>
                        <td className="px-4 py-3 text-indigo-400">{fmt(parseFloat(c.ctr),'pct')}</td>
                        <td className="px-4 py-3 text-green-400 font-semibold">{c.conversions||0}</td>
                        <td className={`px-4 py-3 font-semibold ${(c.roi||0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>{fmt(parseFloat(c.roi),'pct')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
